import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CREATE_ENTITY_CONFIG_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  VIEW_ENTITY_TYPE_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';

export default {
  name: 'EntityConfigDetail',
  mixins: [alert_mixin],
  data: () => ({
    loading: false,
    entity_config: {},
    entity_type: {},
    form_bindings: [],
    workflow_bindings: [],
    rule_bindings: [],
    version_history: []
  }),
  computed: {
    entity_type_id() {
      return Number(this.$route.params.entityTypeId);
    },
    entity_type_name() {
      return (
        this.entity_type.entity_type_name ||
        this.entity_config.entity_type_name ||
        this.entity_config.entity_type?.entity_type_name ||
        'Entity'
      );
    },
    summary_items() {
      return [
        { label: 'Forms', count: this.form_bindings.length, icon: 'bi-file-earmark-text' },
        { label: 'Workflow', count: this.workflow_bindings.length, icon: 'bi-diagram-3' },
        { label: 'Rule', count: this.rule_bindings.length, icon: 'bi-sliders' }
      ];
    },
    formatted_last_modified() {
      const rawDate = this.entity_config.date_updated || this.entity_config.date_created;
      if (!rawDate) return 'Recently created';

      const parsed = new Date(rawDate);
      if (Number.isNaN(parsed.getTime())) return rawDate;

      return parsed.toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    recent_activities() {
      const toneMap = {
        form: {
          icon: 'bi-arrow-repeat',
          toneClass: 'activity-tone-primary',
          title: 'Form binding updated'
        },
        workflow: {
          icon: 'bi-lightning-charge',
          toneClass: 'activity-tone-warning',
          title: 'Workflow adjusted'
        },
        rule: {
          icon: 'bi-shield-check',
          toneClass: 'activity-tone-slate',
          title: 'Rule logic modified'
        }
      };

      return (this.version_history || []).slice(0, 3).map((entry) => {
        const tone = toneMap[entry.binding_type] || {
          icon: 'bi-clock-history',
          toneClass: 'activity-tone-slate',
          title: 'Configuration updated'
        };

        return {
          id: entry.id,
          icon: tone.icon,
          toneClass: tone.toneClass,
          title: `${tone.title}${entry.version ? ` v${entry.version}` : ''}`,
          reason: entry.change_reason || 'Configuration changes were recorded.',
          timestamp: this.formatDate(entry.date_created)
        };
      });
    }
  },
  mounted() {
    this.load_entity_config_detail();
  },
  methods: {
    parse_response_data(payload) {
      return typeof payload === 'string' ? JSON.parse(payload) : payload;
    },

    formatDate(value) {
      if (!value) return 'Recently';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    go_to_bind_subsystems() {
      this.$router.push({
        name: 'EntitySubsystemBindings',
        params: {
          entityTypeId: this.entity_type_id
        }
      });
    },
    build_subsystem_route(route_name) {
      const query = {
        entityTypeId: this.entity_type_id ? String(this.entity_type_id) : '',
        entityTypeName: this.entity_type_name
      };
      const route = {
        name: route_name,
        query
      };

      return route;
    },
    go_to_create_form() {
      this.$router.push(this.build_subsystem_route('CreateForm'));
    },
    go_to_create_workflow() {
      this.$router.push(this.build_subsystem_route('CreateWorkflow'));
    },
    go_to_compliance_management() {
      this.$router.push(this.build_subsystem_route('ComplianceManagement'));
    },
    go_to_view_template() {
      this.$router.push(this.build_subsystem_route('ViewTemplate'));
    },
    go_to_verification_services() {
      this.$router.push(this.build_subsystem_route('VerificationServices'));
    },
    go_to_manage_sub_entities() {
      this.$router.push(this.build_subsystem_route('ManageSubEntities'));
    },

    async fetch_entity_type() {
      const response = await axios.get(
        VIEW_ENTITY_TYPE_URL,
        get_request_config({
          params: {
            entity_type_id: this.entity_type_id
          }
        })
      );
      const responseData = this.parse_response_data(response.data);
      const items = responseData.data || [];
      this.entity_type = Array.isArray(items)
        ? (items.find((item) => Number(item.id) === this.entity_type_id) || items[0] || {})
        : items;
    },

    async fetch_full_config() {
      const response = await axios.get(
        GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
        get_request_config({
          params: {
            entity_type_id: this.entity_type_id
          }
        })
      );
      const responseData = this.parse_response_data(response.data);
      return responseData.data || {};
    },

    async ensure_entity_config_exists() {
      return this.fetch_full_config().catch(async (error) => {
        if (error?.response?.status !== 404) {
          throw error;
        }

        await axios.post(
          CREATE_ENTITY_CONFIG_URL,
          {
            entity_type_id: this.entity_type_id
          },
          get_request_config()
        );

        return this.fetch_full_config();
      });
    },

    async load_entity_config_detail() {
      this.loading = true;
      const loader = await this.show_loader('Loading entity config...');

      try {
        await this.fetch_entity_type();
        const configData = await this.ensure_entity_config_exists();

        this.entity_config = configData.entity_config || {};
        this.form_bindings = configData.form_bindings || [];
        this.workflow_bindings = configData.workflow_bindings || [];
        this.rule_bindings = configData.rule_bindings || [];
        this.version_history = configData.version_history || [];
      } catch (error) {
        const responseData = this.parse_response_data(error?.response?.data || {});
        this.show_error(responseData.message || 'Failed to load entity configuration.');
      } finally {
        await this.close_loader(loader);
        this.loading = false;
      }
    }
  }
};
